import queryString from 'query-string'
import pluralize from 'pluralize'

import { isEmpty, startCase } from 'lodash'
import { apiErrorMessage, apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { PERMISSION_LABELS } from '@/views/Organizations/organization.schema'
import {
  MAX_LISTED_UNREACHED_USERS,
  SUPPORT_ACTION_DELIVERED,
  SUPPORT_ACTION_OPTIONS,
} from '@/views/Organizations/components/supportActions.constants'
import {
  setLoading,
  setUserPermissionColumns,
  setUsers,
  setUserAudit,
} from './organizationUsers.redux'

// "Failed to <action>." plus the backend's reason when it sent one. A refused (403)
// request shows the permission message instead (see toast.helpers).
const toastFailure = (action, err) =>
  toast.error(`Failed to ${action}. ${err?.error_message ? err.error_message : ''}`, { cause: err })

export const fetchUsers =
  ({ queryParam, setLoading, organizationId, dispatchAction }) =>
  async (dispatch) => {
    if (setLoading) setLoading(true)
    const loadUsersQueryString =
      queryParam && queryParam !== 'active'
        ? `?${queryString.stringify({ [queryParam]: true })}`
        : ''
    try {
      const loadUsers = await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/users${loadUsersQueryString}`,
        'GET',
        dispatch
      )
      const { users } = loadUsers
      dispatch({ type: dispatchAction || 'organizations/setUsers', payload: users })
    } catch (err) {
      console.error('fetchUsers failed', err)
    } finally {
      if (setLoading) setLoading(false)
    }
  }

export const fetchUserAudit = (userId) => async (dispatch) => {
  dispatch(setLoading({ userAudit: true }))
  dispatch(setUserAudit([]))

  try {
    const userAudit = await fetchingAPI(
      `${apiService.core}/api/users/${userId}/audit`,
      'GET',
      dispatch
    )
    dispatch(setUserAudit(userAudit))
  } catch (err) {
    console.error('fetchUserAudit failed', err)
  } finally {
    dispatch(setLoading({ userAudit: false }))
  }
}

export const createUser =
  ({ organizationId, user, updateUsers, setLoading }) =>
  async (dispatch) => {
    setLoading(true)

    // Strip form-state-only fields and convert empty strings to null
    // so the API receives clean data
    const { validateOnMount, uncategorizedTags, access, ...apiFields } = user
    const cleanedUser = Object.fromEntries(
      Object.entries(apiFields).map(([key, value]) => [key, value === '' ? null : value])
    )

    try {
      await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/users`,
        'POST',
        dispatch,
        JSON.stringify(cleanedUser)
      )
      toast.success('User created successfully')
      updateUsers(organizationId)

      return true
    } catch (err) {
      toast.error(`Failed to create user. ${err?.error_message ? err.error_message : ''}`, {
        cause: err,
      })

      // Lets the form stay open, so a refused create doesn't lose the input.
      return false
    } finally {
      setLoading(false)
    }
  }

export const bulkUpdatePermissions =
  ({ userIds, permissions, organizationId, onSuccess }) =>
  async (dispatch) => {
    const payload = {
      user_ids: userIds,
      permissions: permissions.map((permission) => ({ value: permission.value })),
      organization_id: organizationId,
    }

    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/users/permissions/bulk`,
        'PATCH',
        dispatch,
        JSON.stringify(payload)
      )
      if (!isEmpty(response?.skipped)) {
        toast.warning(
          `Permissions updated. ${response.skipped.length} deactivated user(s) skipped.`
        )
      } else {
        toast.success('Permissions updated successfully!')
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      toastFailure('update permissions', err)
    }
  }

export const bulkAssignTags =
  ({ userIds, tagIds, organizationId, onSuccess }) =>
  async (dispatch) => {
    const payload = {
      user_ids: userIds,
      tag_ids: tagIds,
      organization_id: organizationId,
    }

    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/users/bulk/tags`,
        'PATCH',
        dispatch,
        JSON.stringify(payload)
      )
      if (!isEmpty(response?.skipped)) {
        toast.warning(`Tags assigned. ${response.skipped.length} deactivated user(s) skipped.`)
      } else {
        toast.success('Tags assigned successfully!')
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      toastFailure('assign tags', err)
    }
  }

export const bulkAssignPlaybook =
  ({ userIds, configCid, onSuccess }) =>
  async (dispatch) => {
    const payload = {
      user_ids: userIds,
      config_cid: configCid,
    }

    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/users/batch`,
        'PATCH',
        dispatch,
        JSON.stringify(payload)
      )
      if (!isEmpty(response?.skipped)) {
        toast.warning(`Playbook assigned. ${response.skipped.length} deactivated user(s) skipped.`)
      } else {
        toast.success('Playbook assigned successfully!')
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      toastFailure('assign playbook', err)
    }
  }

export const bulkActivateUsers =
  ({ userIds, organizationId, onSuccess }) =>
  async (dispatch) => {
    const payload = {
      user_ids: userIds,
      organization_id: organizationId,
      action: 'activate',
    }

    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/users/bulk/status`,
        'PATCH',
        dispatch,
        JSON.stringify(payload)
      )
      if (!isEmpty(response?.skipped)) {
        toast.warning(`Users activated. ${response.skipped.length} user(s) skipped.`)
      } else {
        toast.success('Users activated successfully!')
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      toastFailure('activate users', err)
    }
  }

export const bulkDeactivateUsers =
  ({ userIds, organizationId, onSuccess }) =>
  async (dispatch) => {
    const payload = {
      user_ids: userIds,
      organization_id: organizationId,
      action: 'deactivate',
    }

    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/users/bulk/status`,
        'PATCH',
        dispatch,
        JSON.stringify(payload)
      )
      if (!isEmpty(response?.skipped)) {
        toast.warning(`Users deactivated. ${response.skipped.length} user(s) skipped.`)
      } else {
        toast.success('Users deactivated successfully!')
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      toastFailure('deactivate users', err)
    }
  }

// Sends a support action to the users' desktop apps. Resolves true once the request went
// through, even if some users weren't connected, so the caller knows to close its modal.
export const sendSupportAction =
  ({ usernames, action }) =>
  async (dispatch) => {
    const { label } = SUPPORT_ACTION_OPTIONS.find((option) => option.value === action)

    try {
      const { results } = await fetchingAPI(
        `${apiService.core}/api/desktop_commands`,
        'POST',
        dispatch,
        JSON.stringify({ agents: usernames, command: action }),
        undefined,
        {},
        // core-service only takes these from org 1, so send the user's own org, not the one
        // they're viewing
        true
      )
      const notReached = results
        .filter((result) => result.status !== SUPPORT_ACTION_DELIVERED)
        .map((result) => result.username)
      const userCount = pluralize('user', results.length, true)

      if (isEmpty(notReached)) {
        toast.success(`${label} sent to ${userCount}`)
      } else {
        const listedNames = notReached.slice(0, MAX_LISTED_UNREACHED_USERS).join(', ')
        const unlistedCount = notReached.length - MAX_LISTED_UNREACHED_USERS
        const unlisted = unlistedCount > 0 ? ` and ${unlistedCount} more` : ''

        toast.warning(
          `${label} sent to ${results.length - notReached.length} of ${userCount}. Not reached: ${listedNames}${unlisted}`
        )
      }

      return true
    } catch (err) {
      toast.error(apiErrorMessage(err, `Failed to send ${label}`))

      return false
    }
  }

export const patchUser = (userId, data) => async (dispatch) => {
  try {
    await fetchingAPI(
      `${apiService.core}/api/users/${userId}`,
      'PATCH',
      dispatch,
      JSON.stringify(data)
    )
    toast.success('User updated successfully!')
  } catch (err) {
    toastFailure('update user', err)
  }
}

export const updateUser =
  ({ userId, userData, loadUsersData, queryParam, setLoading, toastMessage }) =>
  async (dispatch, getState) => {
    if (setLoading) setLoading(true)
    const userDataToSubmit = { ...userData }
    // if tag objects are passed instead of ids, then convert object into ids.
    if (userDataToSubmit?.tags?.length > 0 && typeof userDataToSubmit.tags[0] === 'object') {
      userDataToSubmit.tags = userDataToSubmit.tags.map((tag) => tag && tag.id)
    }

    try {
      const { organization_id } = await fetchingAPI(
        `${apiService.core}/api/users/${userId}`,
        'PATCH',
        dispatch,
        JSON.stringify(userDataToSubmit)
      )
      const oldUsers = getState().organizationUsers.users
      const users = oldUsers.map((user) =>
        user.id === userId ? { ...userData, tags: user.tags } : user
      )
      dispatch(setUsers(users))
      dispatch(loadUsersData({ organizationId: organization_id, queryParam, setLoading }))

      if (toastMessage) {
        toast.success(toastMessage)
      }
    } catch (err) {
      toastFailure('update user', err)
    } finally {
      if (setLoading) setLoading(false)
    }
  }

export const restoreUser =
  ({ userId, organizationId, loadUsersData, setLoading, queryParam }) =>
  async (dispatch) => {
    try {
      await fetchingAPI(`${apiService.core}/api/users/${userId}`, 'POST', dispatch)

      dispatch(loadUsersData({ organizationId, queryParam, setLoading }))
    } catch (err) {
      toastFailure('restore user', err)
    }
  }

// TODO: Refactor toggleLoading, update to async/await
export const exportUsers =
  ({ organizationId, data, toggleLoading }) =>
  (dispatch) => {
    const body = JSON.stringify(data)
    toggleLoading()
    fetchingAPI(`${apiService.core}/api/export/${organizationId}/users`, 'POST', dispatch, body)
      .then((csvStream) => {
        // get stream reader from fetch response body stream
        const responseReader = csvStream.getReader()
        const streamProgress = { data: '' }
        // this function reads stream, then if done == true we download and clean up.
        const readStream = () =>
          responseReader.read().then(({ value, done }) => {
            if (done) {
              const encodedUri = URL.createObjectURL(
                new Blob([streamProgress.data], { type: 'text/csv' })
              )
              const link = document.createElement('a')
              link.setAttribute('href', encodedUri)
              link.setAttribute('download', 'users.csv')
              document.body.appendChild(link) // Required for FF
              link.click()
              document.body.removeChild(link)

              return toggleLoading()
            }
            // stream comes in as uint8array
            const decodedCsvData = new TextDecoder('utf-8').decode(value)
            streamProgress.data += decodedCsvData

            return readStream()
          })
        readStream()
      })
      .catch(() => {
        toggleLoading()
      })
  }

export const fetchUserPermissions = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ userPermissions: true }))

  try {
    const permissions = await fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/user-permissions`,
      'GET',
      dispatch
    )

    const COLUMN_LABEL_OVERRIDES = {
      code: { value: 'voip_user_id', label: 'VOIP User ID' },
      username: { label: 'Email' },
      deleted: { value: 'user_status', label: 'User Status' },
      config_cid: { value: 'playbook', label: 'Playbook' },
      newrez_complaints_enabled: { label: 'Complaints Enabled' },
    }

    const formattedColumns = permissions.map((column) => {
      const override = COLUMN_LABEL_OVERRIDES[column]
      if (override) {
        return { label: override.label, value: override.value || column }
      }

      if (PERMISSION_LABELS[column]) {
        return { label: PERMISSION_LABELS[column], value: column }
      }

      return { label: startCase(column), value: column }
    })
    formattedColumns.push({ label: 'Tags', value: 'tags' })

    dispatch(setUserPermissionColumns(formattedColumns))
  } catch (err) {
    console.error('fetchUserPermissions failed', err)
  } finally {
    dispatch(setLoading({ userPermissions: false }))
  }
}
